import { z } from "zod/v4";

import { JSONValueSchema } from "../common";
import { MediaPartSchema } from "../media";

// Error structure for tool execution failures
export const ToolErrorSchema = z.object({
  name: z.string(),
  message: z.string(),
  /**
   * How the tool framework should log this failure, when the tool knows better
   * than the framework's name-based default.
   *
   *  - `"error"` / `"warn"` — log at that level.
   *  - `"none"` — don't log; the tool has already reported it with better
   *    context than the framework could.
   *  - absent — the framework classifies by `name` (its own generic set).
   *
   * Lets a tool keep a known, unactionable failure off the error surface —
   * where it would inflate the error rate and page on-call — without the
   * framework needing to know that tool's error vocabulary.
   */
  logClass: z.enum(["error", "warn", "none"]).optional(),
  /**
   * Structured data behind a typed refusal — the `data` of node-backend's
   * error envelope, such as a 409's conflict DTO with its machine-readable
   * `reason` — so the model can branch on it instead of parsing `message`.
   * Absent when the failure carries no structure.
   */
  details: JSONValueSchema.optional(),
});

export type ToolError = z.infer<typeof ToolErrorSchema>;

const ToolResultTextSchema = z.object({
  type: z.literal("text"),
  value: z.string(),
});
type ToolResultTextType = z.infer<typeof ToolResultTextSchema>;

export const RequiredToolExecutionJsonResultDataSchema = z.object({
  executionTime: z.number(),
});
export type RequiredToolExecutionJsonResultData = z.infer<
  typeof RequiredToolExecutionJsonResultDataSchema
>;

const val = z
  .object({
    results: JSONValueSchema,
  })
  .and(z.object({ ...RequiredToolExecutionJsonResultDataSchema.shape }));

const ToolResultJSONSchema = z.object({
  type: z.literal("json"),
  value: val,
});

export type ToolExecutionResponse<T> =
  | { output: T; success: true }
  | { output: ToolError; success: false };

export type ToolResultJSONType<T extends object = object> = {
  type: "json";
  value: {
    results: T;
  } & RequiredToolExecutionJsonResultData;
};

const ToolResultErrorTextSchema = z.object({
  type: z.literal("error-text"),
  value: z.string(),
});
export type ToolResultErrorTextType = z.infer<typeof ToolResultErrorTextSchema>;

const ToolResultErrorJSONSchema = z.object({
  type: z.literal("error-json"),
  value: z
    .object({
      results: z.unknown(),
    })
    .and(z.object({ ...RequiredToolExecutionJsonResultDataSchema.shape })),
});

export type ToolResultErrorJSONType<T extends object = object> = {
  type: "error-json";
  value: { results: T } & RequiredToolExecutionJsonResultData;
};

const ToolResultMediaSchema = z.object({
  type: z.literal("content"),
  value: z.array(
    z.union([
      z.object({
        type: z.literal("text"),
        text: z.string(),
      }),
      MediaPartSchema,
    ])
  ),
});

export type ToolResultMediaType = z.infer<typeof ToolResultMediaSchema>;

export const ToolResultOutputSchema = z.discriminatedUnion("type", [
  ToolResultTextSchema,
  ToolResultJSONSchema,
  ToolResultErrorTextSchema,
  ToolResultErrorJSONSchema,
  ToolResultMediaSchema,
]);

export type ToolResultOutput<T extends object = object> =
  | ToolResultTextType
  | ToolResultJSONType<T>
  | ToolResultErrorTextType
  | ToolResultErrorJSONType<T>
  | ToolResultMediaType;
